import React, { useState, useEffect } from "react";
import { Modal } from "antd";
import { makeRequest } from "../../../api/api";
import "../../../css/biofuelCompositionModal.css";
import { clearBiofuelSession } from "../../../utils/biofuelSession";


// DEFAULT VALUES
const initialState = {
  biofuelName: "",
  fossilFuelName: "",
  eValue: "",
  fuelType: "",
  blendRatio: "",
  year: new Date().getFullYear(), 
};

export default function BiofuelCompositionModal({ visible, onClose, onSubmit, selectedYear }) {
  const [formData, setFormData] = useState(initialState);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && selectedYear) {
      setFormData(prev => ({ ...prev, year: selectedYear }));
    }
  }, [visible, selectedYear]);

  const handleChange = (field, value) => {
    if (field === "biofuelName") {
      value = value.toUpperCase();
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Convert UI fuelType → backend format
  const mapFuelTypeForApi = (fuelType) => {
    if (!fuelType) return "";

    switch (fuelType) {
      case "BioDiesel":
        return "BioDiesel";
      case "HVO":
        return "HVO";
      default:
        return fuelType.charAt(0).toUpperCase() + fuelType.slice(1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const upperCaseBiofuelName = formData.biofuelName.toUpperCase();
    sessionStorage.setItem("lastBiofuelName", upperCaseBiofuelName);

    // BASIC VALIDATION
    if (!formData.fuelType) {
      alert("Select Biofuel Type");
      return;
    }
    if (!formData.blendRatio) {
      alert("Enter Blend Ratio");
      return;
    }
    if (!formData.year || formData.year.length < 4) {
      alert("Enter valid Year");
      return;
    }

    const apiFuelType = mapFuelTypeForApi(formData.fuelType);
    const apiBlendRatio = formData.blendRatio;

    const url = `api/ems/get_fuel_factors_for_biofuel/?biofuel_name=${encodeURIComponent(
      upperCaseBiofuelName
    )}&fossil_fuel_name=${encodeURIComponent(
      formData.fossilFuelName
    )}&e_value=${encodeURIComponent(
      formData.eValue
    )}&biofuel_type=${encodeURIComponent(
      apiFuelType
    )}&blend_ratio=${encodeURIComponent(
      apiBlendRatio
    )}&current_year=${encodeURIComponent(formData.year)}`;

    try {
      setLoading(true);

      const result = await makeRequest("get", url);

      console.log("Biofuel API Result:", result);

      if (onSubmit) {
        onSubmit({ 
          data: result, 
          formData: {
            ...formData,
            biofuelName: upperCaseBiofuelName
          }
        });
      }

      setFormData(initialState);
    } catch (error) {
      console.error("API Error in BiofuelCompositionModal:", error);
      alert("API failed. Check console.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(initialState);
    clearBiofuelSession();
    onClose();
  };

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={760}
      centered
      closable={false}
      bodyStyle={{ padding: 0 }}
    >
      <div className="biofuel-modal">
        {/* HEADER */}
        <div className="biofuel-modal__header">
          <h1 className="biofuel-modal__title">Biofuel Composition</h1>
          <button
            type="button"
            className="biofuel-modal__close-btn"
            onClick={handleClose}
          >
            ✕
          </button>
        </div>

        {/* FORM */}
        <form onSubmit={handleSubmit}>
          <div className="biofuel-modal__section">
            <h2 className="biofuel-modal__section-title">Fuel Information</h2>

            <div className="biofuel-modal__grid">

              {/* Biofuel Name */}
              <div className="biofuel-modal__field">
                <label>Biofuel Name</label>
                <input
                  type="text"
                  placeholder="Enter biofuel name"
                  value={formData.biofuelName}
                  onChange={(e) => handleChange("biofuelName", e.target.value)}
                  style={{ textTransform: 'uppercase' }}
                  required
                />
              </div>

              {/* Fossil Fuel Name */}
              <div className="biofuel-modal__field">
                <label>Fossil Fuel Name</label>
                <select
                  value={formData.fossilFuelName}
                  onChange={(e) => handleChange("fossilFuelName", e.target.value)}
                  required
                >
                  <option value="">Select Fuel</option>
                  <option value="HFO">HFO</option>
                  <option value="VLSFO">VLSFO</option>
                  <option value="ULSFO">ULSFO</option>
                  <option value="LFO">LFO</option>
                  <option value="MDO">MDO</option>
                  <option value="LPG-AVERAGE">LPG-AVERAGE</option>
                  <option value="LNG-BOILER">LNG-BOILER</option>
                  <option value="LNG-OTHERS">LNG-OTHERS</option>
                  <option value="LNG-AE">LNG-AE</option>
                  <option value="LNG-ME">LNG-ME</option>
                  <option value="LPG-BUTANE">LPG-BUTANE</option>
                  <option value="LPG-PROPANE">LPG-PROPANE</option>
                  <option value="METHANOL">METHANOL</option>
                  <option value="ETHANOL">ETHANOL</option>
                </select>
              </div>

              {/* E-value */}
              <div className="biofuel-modal__field">
                <label>E-Value</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="Enter E-value"
                  value={formData.eValue}
                  onChange={(e) => handleChange("eValue", e.target.value)}
                  required
                />
              </div>

              {/* Biofuel Type */}
              <div className="biofuel-modal__field">
                <label>Bio Fuel Type</label>
                <select
                  value={formData.fuelType}
                  onChange={(e) => handleChange("fuelType", e.target.value)}
                  required
                >
                  <option value="">Select Biofuel type</option>
                  <option value="BioDiesel">BioDiesel</option>
                  <option value="Bio-LNG">Bio-LNG</option>
                  <option value="HVO">HVO</option>
                </select>
              </div>

              {/* Blend Ratio */}
              <div className="biofuel-modal__field">
                <label>Blend Ratio (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  placeholder="Enter blend ratio"
                  value={formData.blendRatio}
                  onChange={(e) => handleChange("blendRatio", e.target.value)}
                  required
                />
              </div>

              {/* Year */}
              <div className="biofuel-modal__field">
                <label>Year</label>
                <input
                  type="number"
                  min="1900"
                  max="2100"
                  value={formData.year}
                  onChange={(e) => handleChange("year", e.target.value)}
                  required
                />
              </div>

            </div>
          </div>

          {/* FOOTER */}
          <div className="biofuel-modal__button-group">
            <button
              type="button"
              className="biofuel-modal__btn biofuel-modal__btn--cancel"
              onClick={handleClose}
              disabled={loading}
            >
              ← Close
            </button>

            <button
              type="submit"
              className="biofuel-modal__btn biofuel-modal__btn--submit"
              disabled={
                loading ||
                !formData.biofuelName ||
                !formData.fuelType ||
                !formData.blendRatio
              }
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
